/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import Spinner from "../components/Spinner";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import ExpressionEntry, { DisplayFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import UnsignedIntEntry from "../datatypeComponents/UnsignedIntEntry";
import PositiveIntEntry from "../datatypeComponents/PositiveIntEntry";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { ManageInclusionExclusionEnhancedCharacteristicTables } from "./editReferencedResourceComponents/GroupResourceEdit";
import generateNarrativeForGroupResource from "./generateNarrativeFunctions/generateNarrativeForGroupResource";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";

const groupDotTypeValues = ['person', 'animal', 'practitioner', 'device', 'careteam', 'healthcareservice', 'location', 'organization', 'relatedperson', 'specimen', 'medication', 'substance', 'biologicallyDerivedProduct', 'nutritionProduct'];
const groupDotMembershipValues = ['conceptual', 'definitional', 'enumerated'];
const groupDotManagingEntityResourceTypes = ['Organization', 'RelatedPerson', 'Practitioner', 'PractitionerRole'];
const groupDotCombinationMethodValues = ['all-of', 'any-of', 'at-least', 'at-most', 'except-subset'];

const GroupDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    let toolDisplayName = "Group";
    let isCohortDefinitionProfile;
    let isConceptualCohortDefinitionProfile;
    let isAnEnumeratedGroupProfile;
    let isNetEffectContributionsProfile;
    let isMetaanalysisStudyGroupProfile;
    let isEvidenceReportSubjectProfile;
    if (resourceJson.meta?.profile?.length) {
        for (const profile of resourceJson.meta.profile) {
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/cohort-definition" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-eligibility-criteria" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/recommendation-eligibility-criteria"
            ) {
                isCohortDefinitionProfile = true;
                toolDisplayName = "CohortDefinition";
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/conceptual-cohort-definition" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/systematic-review-eligibility-criteria" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/metaanalysis-eligibility-criteria" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/conceptual-exposure-definition" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/conceptual-comparator-definition" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/outcome-definition" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/metaanalysis-outcome-definition" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/search-strategy" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/absence-of-exposure-definition") {
                isConceptualCohortDefinitionProfile = true;
                toolDisplayName = "ConceptualCohortDefinition";
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/exposure-group" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparator-group" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-group"
            ) {
                isAnEnumeratedGroupProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-contributions") {
                isNetEffectContributionsProfile = true;
                toolDisplayName = "NetEffectContributions";
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/metaanalysis-study-group") {
                isMetaanalysisStudyGroupProfile = true;
                toolDisplayName = "MetaanalysisStudyGroup";
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/evidence-report-subject" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-evidence-report-subject"
            ) {
                isEvidenceReportSubjectProfile = true;
                toolDisplayName = "EvidenceReportSubject";
            }
        }
    }

    const [groupResourceState, setGroupResourceState] = useState({});
    const [characteristicExpressionState, setCharacteristicExpressionState] = useState({});

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR " + toolDisplayName + " - " + displayTitle));
        dispatch(changeTopNavigationElements(["Summary", "Characteristic Combination",
            "Inclusion Criteria", "Exclusion Criteria", "Members"]));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setGroupResourceState(olderVersionJson);
                let starterCharacteristicExpression = null;
                if (Array.isArray(olderVersionJson.extension)) {
                    for (let extension of olderVersionJson.extension) {
                        if (extension.url === "http://hl7.org/fhir/StructureDefinition/characteristicExpression") {
                            starterCharacteristicExpression = extension.valueExpression;
                            break;
                        }
                    }
                }
                setCharacteristicExpressionState({ "valueExpression": starterCharacteristicExpression });
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            let revisedResourceJson = JSON.parse(JSON.stringify(resourceJson));
            if (!revisedResourceJson.characteristic) {
                revisedResourceJson.characteristic = [];
            }
            setGroupResourceState(revisedResourceJson);
            let starterCharacteristicExpression = null;
            if (Array.isArray(resourceJson.extension)) {
                for (let extension of resourceJson.extension) {
                    if (extension.url === "http://hl7.org/fhir/StructureDefinition/characteristicExpression") {
                        starterCharacteristicExpression = extension.valueExpression;
                        break;
                    }
                }
            }
            setCharacteristicExpressionState({ "valueExpression": starterCharacteristicExpression });
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: Group " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect((() => {
        if (characteristicExpressionState.valueExpression) {
            setGroupResourceState(prevState => {
                let newExtension = JSON.parse(JSON.stringify(prevState.extension));
                if (!newExtension) {
                    newExtension = [];
                }
                let expressionExtensionFound = false;
                for (let extension of newExtension) {
                    if (extension.url === "http://hl7.org/fhir/StructureDefinition/characteristicExpression") {
                        expressionExtensionFound = true;
                        extension.valueExpression = characteristicExpressionState.valueExpression;
                    }
                }
                if (!expressionExtensionFound) {
                    newExtension.push({
                        "url": "http://hl7.org/fhir/StructureDefinition/characteristicExpression",
                        "valueExpression": characteristicExpressionState.valueExpression
                    })
                }
                return { ...prevState, "extension": newExtension };
            });
        }
    }), [characteristicExpressionState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "extension", "ArrayOfExtension", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.extension]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(groupResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = groupResourceState.title;
            newResourceDetail.displayTitle = groupResourceState.title ||
                groupResourceState.name || newResourceDetail.displayTitle;
            if (groupResourceState.title !== undefined &&
                previousFinalValue !== groupResourceState.title) {
                console.log("resourceUpdate for Group.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    groupResourceState.title);
            }
        }
    }, [groupResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = groupResourceState.name;
            newResourceDetail.displayTitle = groupResourceState.title ||
                groupResourceState.name || newResourceDetail.displayTitle;
            if (groupResourceState.name !== undefined &&
                previousFinalValue !== groupResourceState.name) {
                console.log("resourceUpdate for Group.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "name",
                    previousInitialValue,
                    groupResourceState.name);
            }
        }
    }, [groupResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "membership", "code", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.membership]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "type", "code", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "quantity", "unsignedInt", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.quantity]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "code", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.code]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "managingEntity", "Reference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.managingEntity]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "combinationMethod", "code", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.combinationMethod]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "combinationThreshold", "positiveInt", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.combinationThreshold]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(groupResourceState, "characteristic", "ArrayOfGroupCharacteristic", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [groupResourceState.characteristic]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {groupResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="summary">Summary</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <StringEntry elementName='title' fieldLabel='Title'
                                    startingValue={groupResourceState.title} setResourceState={setGroupResourceState} />
                                <StringEntry elementName='name' fieldLabel='Name'
                                    startingValue={groupResourceState.name} setResourceState={setGroupResourceState} />
                            </>
                            :
                            <>
                                <span><b>Title: </b> {groupResourceState.title ? groupResourceState.title : <>Not stated</>}</span>
                                <br />
                                <span><b>Name: </b> {groupResourceState.name ? groupResourceState.name : <>Not stated</>}</span>
                                <br />
                            </>
                    }
                    {
                        editMode ?
                            <>
                                {!groupResourceState.membership &&
                                    <p>Select a Membership code for this Group. Choose 'enumerated' if you will report a quantity for the Group or if you will report or reference the Group members. Choose 'definitional' if you will report the characteristics defining membership in the Group and will select a Type code specifying the type of Group members in a FHIR context. Choose 'conceptual' if you are not reporting an enumerated or definitional Group, or if you are reporting a Group without a Type code.</p>
                                }
                                {(isAnEnumeratedGroupProfile && groupResourceState.membership !== "enumerated") &&
                                    <p><b>The membership element does not have a value of 'enumerated', but that is required for the selected Profile.</b></p>
                                }
                                {(isCohortDefinitionProfile && groupResourceState.membership !== "definitional") &&
                                    <p><b>The membership element does not have a value of 'definitional', but that is required for the selected Profile.</b></p>
                                }
                                {(isConceptualCohortDefinitionProfile && groupResourceState.membership !== "conceptual") &&
                                    <p><b>The membership element does not have a value of 'conceptual', but that is required for the selected Profile.</b></p>
                                }
                                {((isEvidenceReportSubjectProfile || isNetEffectContributionsProfile || isMetaanalysisStudyGroupProfile) &&
                                    groupResourceState.membership !== "conceptual") &&
                                    <p><b>The membership element does not have a value of 'conceptual', but that is required for the selected Profile.</b></p>
                                }
                                {((groupResourceState.quantity || groupResourceState.quantity === 0) &&
                                    groupResourceState.membership === "definitional") &&
                                    <p><b>The membership element has a value of 'definitional', but the quantity element has a value.</b></p>
                                }
                                <CodeEntry elementName='membership' fieldLabel='Membership'
                                    allowedValues={groupDotMembershipValues}
                                    startingValue={groupResourceState.membership} setResourceState={setGroupResourceState} />
                                <UnsignedIntEntry elementName='quantity' fieldLabel='Quantity'
                                    startingValue={groupResourceState.quantity} setResourceState={setGroupResourceState} />
                            </>
                            :
                            <>
                                <span><b>Membership/Definitional: </b> {groupResourceState.membership}</span>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                {groupResourceState.quantity && <span><b>Quantity: </b> {groupResourceState.quantity}</span>}
                                <br />
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <CodeEntry elementName='type' fieldLabel='Type'
                                    allowedValues={groupDotTypeValues}
                                    startingValue={groupResourceState.type} setResourceState={setGroupResourceState} />
                                <CodeableConceptEntry elementName='code' fieldLabel='Kind'
                                    startCollapsed
                                    startingValue={groupResourceState.code} setResourceState={setGroupResourceState} />
                            </>
                            :
                            <>
                                <span><b>Type: </b> {
                                    groupResourceState.type && typeof groupResourceState.type === "string" ?
                                        groupResourceState.type
                                        :
                                        <>Not stated</>
                                }</span>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                {groupResourceState.code && <span><b>Kind: </b>{displayStringFromFHIRCodeableConcept(groupResourceState.code)}</span>}
                                <br />
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <MarkdownEntry elementName='description' fieldLabel='Description'
                                    startCollapsed
                                    startingValue={groupResourceState.description} setResourceState={setGroupResourceState} />
                            </>
                            :
                            <>
                                {groupResourceState.description &&
                                    <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={groupResourceState.description} /></span>}
                                <br />
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <ReferenceEntry elementName='managingEntity' fieldLabel='Managing Entity'
                                    referencedResourceTypes={groupDotManagingEntityResourceTypes} startCollapsed
                                    startingValue={groupResourceState.managingEntity} setResourceState={setGroupResourceState} />
                            </>
                            :
                            <>
                                {groupResourceState.managingEntity &&
                                    <span><b>Managing Entity: </b><DisplayFromFHIRReference reference={groupResourceState.managingEntity} /></span>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <ExpressionEntry elementName="valueExpression"
                                    fieldLabel="Characteristics Defined By Expression"
                                    startingValue={characteristicExpressionState.valueExpression}
                                    startCollapsed
                                    setResourceState={setCharacteristicExpressionState} />
                            </>
                            :
                            <>
                                {characteristicExpressionState?.valueExpression && <div>
                                    <h4>Characteristic Expression:</h4>
                                    <DisplayFromFHIRExpression expression={characteristicExpressionState.valueExpression} />
                                </div>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <h4>Narrative Summary</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                        generatedNarrative={generateNarrativeForGroupResource(groupResourceState)} startCollapsed
                                        startingValue={groupResourceState.text} setResourceState={setGroupResourceState} />
                                </div>
                            </>
                            :
                            <>
                                {(groupResourceState.text?.div && groupResourceState.text.status && groupResourceState.text.status !== "empty") && <>
                                    <h4>Narrative Summary:</h4>
                                    <div style={{ marginLeft: "24px" }}>
                                        <DisplayFromFHIRXhtml xhtml={groupResourceState.text.div} />
                                    </div>
                                </>}
                            </>
                    }
                </div>
                <h3 id="characteristic-combination">Characteristic Combination</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <CodeEntry elementName='combinationMethod' fieldLabel='Combination Method'
                                    allowedValues={groupDotCombinationMethodValues}
                                    startingValue={groupResourceState.combinationMethod} setResourceState={setGroupResourceState} />
                                {(groupResourceState.combinationMethod === "at-least" || groupResourceState.combinationMethod === "at-most" ||
                                    groupResourceState.combinationThreshold) &&
                                    <PositiveIntEntry
                                        elementName='combinationThreshold' fieldLabel='Number of characteristics'
                                        startingValue={groupResourceState.combinationThreshold} setResourceState={setGroupResourceState} />}
                            </>
                            :
                            <>
                                {groupResourceState.combinationMethod &&
                                    <span><b>Combination Method: </b>{groupResourceState.combinationMethod}</span>}
                                {groupResourceState.combinationThreshold &&
                                    <span>&nbsp;&nbsp;&nbsp;<b>Number of characteristics: </b>{groupResourceState.combinationThreshold}</span>}
                            </>
                    }
                </div>
                <br />
                {groupResourceState?.id &&
                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={editMode}
                        fhirJson={groupResourceState}
                        setResourceState={setGroupResourceState} />}
                <h3 id="members">Members</h3>
                <div style={{ marginLeft: "24px" }}>
                    {groupResourceState.membership === "enumerated" ?
                        <div>
                            {editMode && <p>There is no editing interface for the membership at this time.</p>}
                            {groupResourceState.member?.length > 0 ?
                                <>
                                    {groupResourceState.member.map((member, memberIndex) => {
                                        if (member.entity === undefined) {
                                            return <p key={memberIndex}>[ERROR: Group.member element missing entity element.]</p>
                                        }
                                        return <div key={memberIndex}>
                                            <DisplayFromFHIRReference reference={member.entity} />
                                        </div>
                                    })}
                                </>
                                :
                                <p>Members list not provided</p>
                            }
                        </div>
                        :
                        <p>This is a descriptive ({groupResourceState.membership || "unclassified"}) Group. Listing members does not apply.</p>
                    }
                </div>
            </div>
        </>}
    </div>
}

export default GroupDisplay;